import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { Plus, ArrowLeft, CheckSquare, ClipboardList } from 'lucide-react';
import { useProject, useProjects } from '@/hooks/use-projects';
import { useTasks, useCreateTask, useReorderTask, useUpdateTask, type TaskFilters } from '@/hooks/use-tasks';
import { useWorkspaceMembers, useWorkspaces } from '@/hooks/use-workspaces';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { useAuth } from '@/hooks/use-auth';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { KanbanBoard } from '@/components/task/kanban-board';
import { TaskListView } from '@/components/task/task-list-view';
import { TaskDetailDrawer } from '@/components/task/task-detail-drawer';
import { TaskFilters as TaskFiltersBar } from '@/components/task/task-filters';
import { ViewToggle } from '@/components/task/view-toggle';
import { BulkActionToolbar } from '@/components/task/bulk-action-toolbar';
import { EmptyState } from '@/components/common/empty-state';
import { KanbanSkeleton, MobileKanbanSkeleton, TaskListSkeleton } from '@/components/common/skeletons';

const taskSearchSchema = z.object({
  view: z.enum(['kanban', 'list']).optional().default('kanban'),
  status: z.string().optional(),
  assignee_id: z.string().optional(),
  priority: z.string().optional(),
  due_from: z.string().optional(),
  due_to: z.string().optional(),
});

export const Route = createFileRoute(
  '/_authenticated/workspace/$workspaceId/projects/$projectId'
)({
  component: ProjectDetailPage,
  validateSearch: taskSearchSchema,
});

function isInputFocused(): boolean {
  const el = document.activeElement;
  if (!el) return false;
  if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') return true;
  if (el.getAttribute('contenteditable') === 'true') return true;
  return false;
}

function ProjectDetailPage() {
  const { workspaceId, projectId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { setActiveWorkspace } = useWorkspaceStore();
  const { user } = useAuth();

  const { data: project, isLoading: projectLoading } = useProject(workspaceId, projectId);
  const { data: allProjects } = useProjects(workspaceId);
  const { data: members } = useWorkspaceMembers(workspaceId);
  const { data: workspaces } = useWorkspaces();
  const isPrivateWorkspace = workspaces?.find((w) => w.id === workspaceId)?.type === 'private';

  // Filters from URL
  const filters: TaskFilters = useMemo(() => ({
    status: search.status,
    assignee_id: search.assignee_id,
    priority: search.priority,
    due_from: search.due_from,
    due_to: search.due_to,
  }), [search]);

  const { data: tasks = [], isLoading: tasksLoading } = useTasks(workspaceId, projectId, filters);
  const createTask = useCreateTask(workspaceId, projectId);
  const reorderTask = useReorderTask(workspaceId, projectId);
  const updateTask = useUpdateTask(workspaceId, projectId);

  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [focusedTaskId, setFocusedTaskId] = useState<string | null>(null);
  const [quickAddOpen, setQuickAddOpen] = useState(false);

  // Bulk selection state
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (workspaceId) setActiveWorkspace(workspaceId);
  }, [workspaceId, setActiveWorkspace]);

  // Member map for displaying assignee info
  const memberMap = useMemo(() => {
    if (!members) return new Map();
    return new Map(
      members.map((m) => [m.userId, { name: m.displayName, email: m.email }])
    );
  }, [members]);

  // Keyboard shortcut: N to quick add
  useHotkeys('n', () => {
    const active = document.activeElement;
    if (
      active?.tagName === 'INPUT' ||
      active?.tagName === 'TEXTAREA' ||
      active?.getAttribute('contenteditable')
    ) return;
    setQuickAddOpen(true);
  });

  // Escape to exit select mode or deselect focused task
  useHotkeys('escape', () => {
    if (selectMode) {
      setSelectMode(false);
      setSelectedIds(new Set());
    } else if (focusedTaskId) {
      setFocusedTaskId(null);
    }
  });

  // Task status shortcuts (1-4)
  useHotkeys('1', () => {
    if (!focusedTaskId || isInputFocused()) return;
    updateTask.mutate({ taskId: focusedTaskId, status: 'todo' });
  });
  useHotkeys('2', () => {
    if (!focusedTaskId || isInputFocused()) return;
    updateTask.mutate({ taskId: focusedTaskId, status: 'in_progress' });
  });
  useHotkeys('3', () => {
    if (!focusedTaskId || isInputFocused()) return;
    updateTask.mutate({ taskId: focusedTaskId, status: 'done' });
  });
  // E or Enter to open task detail
  useHotkeys('e', () => {
    if (!focusedTaskId || isInputFocused()) return;
    setSelectedTaskId(focusedTaskId);
  });
  useHotkeys('enter', () => {
    if (!focusedTaskId || isInputFocused()) return;
    setSelectedTaskId(focusedTaskId);
  });

  const handleViewChange = useCallback(
    (view: 'kanban' | 'list') => {
      navigate({
        to: '.',
        search: { ...search, view },
      });
    },
    [navigate, search]
  );

  const handleFiltersChange = useCallback(
    (newFilters: TaskFilters) => {
      navigate({
        to: '.',
        search: { ...search, ...newFilters },
      });
    },
    [navigate, search]
  );

  const handleQuickAdd = useCallback(
    (title: string) => {
      createTask.mutate({ title });
      setQuickAddOpen(false);
    },
    [createTask]
  );

  const handleReorder = useCallback(
    (taskId: string, rank_order: string, status?: string) => {
      reorderTask.mutate({ taskId, rank_order, status });
    },
    [reorderTask]
  );

  const handleSelectToggle = useCallback((taskId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedIds.size === tasks.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(tasks.map((t) => t.id)));
    }
  }, [selectedIds.size, tasks]);

  const handleClearSelection = useCallback(() => {
    setSelectMode(false);
    setSelectedIds(new Set());
  }, []);

  if (projectLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-48 animate-pulse rounded bg-muted" />
        <div className="hidden md:block"><KanbanSkeleton /></div>
        <div className="md:hidden"><MobileKanbanSkeleton /></div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <h1 className="text-2xl font-bold">Project not found</h1>
      </div>
    );
  }

  const view = search.view || 'kanban';
  const hasNoTasks = !tasksLoading && tasks.length === 0;

  return (
    <div className="flex h-full flex-col space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() =>
              navigate({
                to: '/workspace/$workspaceId/projects',
                params: { workspaceId },
              })
            }
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl font-bold tracking-tight">{project.name}</h1>
            <div className="flex items-center gap-2">
              <Badge
                variant="outline"
                className="text-[10px] capitalize"
              >
                {project.archivedAt ? 'Archived' : project.isParked ? 'Parked' : 'Active'}
              </Badge>
              {tasks.length > 0 && (
                <span className="text-xs text-muted-foreground">
                  {tasks.filter((t) => t.status === 'done').length}/{tasks.length} done
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant={selectMode ? 'secondary' : 'outline'}
            size="sm"
            className="h-8"
            onClick={() => {
              setSelectMode(!selectMode);
              setSelectedIds(new Set());
            }}
          >
            <CheckSquare className="mr-1.5 h-3.5 w-3.5" />
            Select
          </Button>
          <Button
            size="sm"
            className="h-8"
            onClick={() => setQuickAddOpen(true)}
          >
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            New Task
          </Button>
          <ViewToggle view={view} onViewChange={handleViewChange} />
        </div>
      </div>

      {/* Filter bar */}
      <TaskFiltersBar
        workspaceId={workspaceId}
        filters={filters}
        onFiltersChange={handleFiltersChange}
        isPrivateWorkspace={isPrivateWorkspace}
      />

      {/* Main content */}
      {tasksLoading ? (
        <>
          <div className="hidden md:block">
            {view === 'kanban' ? <KanbanSkeleton /> : <TaskListSkeleton />}
          </div>
          <div className="md:hidden">
            {view === 'kanban' ? <MobileKanbanSkeleton /> : <TaskListSkeleton />}
          </div>
        </>
      ) : hasNoTasks && !quickAddOpen ? (
        <EmptyState
          icon={<ClipboardList className="h-12 w-12" />}
          title="No tasks yet"
          description="Get started by creating your first task. Press N for a quick add."
          action={{
            label: 'New Task',
            onClick: () => setQuickAddOpen(true),
          }}
        />
      ) : view === 'kanban' ? (
        <KanbanBoard
          tasks={tasks}
          projectIsParked={project.isParked}
          onReorder={handleReorder}
          onTaskClick={selectMode ? handleSelectToggle : (id: string) => { setFocusedTaskId(id); setSelectedTaskId(id); }}
          memberMap={memberMap}
          quickAddOpen={quickAddOpen}
          onQuickAddSubmit={handleQuickAdd}
          onQuickAddCancel={() => setQuickAddOpen(false)}
          selectable={selectMode}
          selectedIds={selectedIds}
          onSelectToggle={handleSelectToggle}
          currentUserId={user?.id}
        />
      ) : (
        <TaskListView
          tasks={tasks}
          onTaskClick={selectMode ? handleSelectToggle : (id: string) => { setFocusedTaskId(id); setSelectedTaskId(id); }}
          memberMap={memberMap}
          quickAddOpen={quickAddOpen}
          onQuickAddSubmit={handleQuickAdd}
          onQuickAddCancel={() => setQuickAddOpen(false)}
          selectable={selectMode}
          selectedIds={selectedIds}
          onSelectToggle={handleSelectToggle}
          onSelectAll={handleSelectAll}
          isPrivateWorkspace={isPrivateWorkspace}
        />
      )}

      {/* Bulk Action Toolbar */}
      {selectMode && (
        <BulkActionToolbar
          workspaceId={workspaceId}
          selectedIds={Array.from(selectedIds)}
          members={members?.map((m) => ({ userId: m.userId, displayName: m.displayName }))}
          projects={allProjects?.filter((p) => p.id !== projectId).map((p) => ({ id: p.id, name: p.name }))}
          isPrivateWorkspace={isPrivateWorkspace}
          onClear={handleClearSelection}
        />
      )}

      {/* Task Detail Drawer */}
      {!selectMode && (
        <TaskDetailDrawer
          taskId={selectedTaskId}
          workspaceId={workspaceId}
          projectId={projectId}
          allTasks={tasks}
          memberMap={memberMap}
          members={members?.map((m) => ({ userId: m.userId, displayName: m.displayName }))}
          isPrivateWorkspace={isPrivateWorkspace}
          onClose={() => setSelectedTaskId(null)}
        />
      )}
    </div>
  );
}
